import type { ClientMessage, ServerMessage } from '../../shared/signaling.js';
import { parseServerMessage } from '../../shared/signaling.js';
import { SignalingConnectionError } from './errors.js';

export type ServerMessageHandler = (message: ServerMessage) => void;
export type Unsubscribe = () => void;

export const signalingUrl = (location: Pick<Location, 'protocol' | 'host'>): string =>
  `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;

/** Connector to the signaling WebSocket. Fans incoming messages out to subscribers. */
export class SignalingClient {
  private readonly handlers = new Set<ServerMessageHandler>();
  private readonly closeHandlers = new Set<() => void>();

  private constructor(private readonly socket: WebSocket) {
    socket.addEventListener('message', (event: MessageEvent<string>) => {
      const message = parseServerMessage(event.data);
      for (const handler of this.handlers) {
        handler(message);
      }
    });
    socket.addEventListener('close', () => {
      for (const handler of this.closeHandlers) {
        handler();
      }
    });
  }

  static connect(url: string): Promise<SignalingClient> {
    return new Promise((resolve, reject) => {
      const socket = new WebSocket(url);
      socket.addEventListener('open', () => resolve(new SignalingClient(socket)), { once: true });
      socket.addEventListener(
        'error',
        () => reject(new SignalingConnectionError('unreachable', `Could not connect to signaling server at ${url}`)),
        { once: true },
      );
    });
  }

  send(message: ClientMessage): void {
    if (this.socket.readyState !== WebSocket.OPEN) {
      throw new SignalingConnectionError('other', 'Signaling socket is not open');
    }
    this.socket.send(JSON.stringify(message));
  }

  onMessage(handler: ServerMessageHandler): Unsubscribe {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  onClose(handler: () => void): Unsubscribe {
    this.closeHandlers.add(handler);
    return () => this.closeHandlers.delete(handler);
  }

  /** Resolves with the next message that satisfies the predicate; rejects on a signaling error message. */
  waitFor<T extends ServerMessage>(predicate: (message: ServerMessage) => message is T): Promise<T> {
    return new Promise((resolve, reject) => {
      const unsubscribe = this.onMessage((message) => {
        if (message.type === 'error') {
          unsubscribe();
          const failure =
            message.code === 'room-not-found' ||
            message.code === 'room-full' ||
            message.code === 'inbox-not-found' ||
            message.code === 'inbox-unauthorized'
              ? message.code
              : 'other';
          reject(new SignalingConnectionError(failure, `${message.code}: ${message.message}`));
          return;
        }
        if (predicate(message)) {
          unsubscribe();
          resolve(message);
        }
      });
    });
  }

  close(): void {
    this.socket.close();
  }
}
